import useSWR from 'swr'
import { useMemo } from 'react'
import { collectionObjectApiPath, isCollectionObjectSlug } from '@/utils/collection-object'

// Every consumer reads `[{ time: unix seconds, vwap: BTC }]`. The field is
// still called `vwap` for the sake of the call sites, but since 2026-09-23
// the number is the FAIR PRICE: the close of the mark series from
// `/collection/:slug/ohlcv` -- the same line the terminal draws, the same
// history the homepage's 7d change is taken from. One source for every
// chart on the site. Runes (no cross-marketplace tape) keep the legacy
// escrow VWAP; collection objects keep their sales endpoint.
// `interval` controls bucket size; `start`/`end` narrow the range.
// The backend enforces per-interval max windows, so the frontend paginates
// for anything wider than those caps by issuing several back-to-back calls.
// Chart base URL. Defaults to prod (turbo). Set NEXT_PUBLIC_CHART_API_URL to
// redirect just the chart endpoint (e.g. http://localhost:3006 for local
// backend testing) without pointing the whole app at a local server.
const TURBO_BASE =
	process.env.NEXT_PUBLIC_CHART_API_URL ||
	process.env.NEXT_PUBLIC_API_URL ||
	'https://turbo.ordinalswallet.com'

export type CollectionVWAPPoint = {
	time: number
	vwap: number
}

export type VWAPInterval = '1h' | '4h' | '1d'

type ChartWindow = { s: number; e: number } | null

export function shouldUseLegacyCollectionChart(collection_slug: string) {
	return collection_slug.toLowerCase().startsWith('rune-')
}

export function getCollectionChartPath(
	collection_slug: string,
	interval: VWAPInterval,
	window: ChartWindow
) {
	if (!collection_slug) return null
	const apiPath = collectionObjectApiPath(collection_slug)

	// Rune sales are not indexed by the cross-marketplace `global-chart`
	// endpoint yet, but the legacy chart endpoint has the Rune price series.
	if (shouldUseLegacyCollectionChart(collection_slug)) {
		return `/${apiPath}/chart`
	}

	if (!window) return null

	if (isCollectionObjectSlug(collection_slug)) {
		// The sales endpoint buckets hourly or daily; four hours is served hourly.
		const salesInterval = interval === '4h' ? '1h' : interval
		return `/${apiPath}/sales?limit=1000&interval=${salesInterval}&start=${window.s}&end=${window.e}`
	}

	return `/${apiPath}/ohlcv?interval=${interval}&denom=sats&series=mark&start=${window.s}&end=${window.e}`
}

/** True when the series is the fair price rather than a VWAP. */
export function isFairPriceSeries(collection_slug: string) {
	return !shouldUseLegacyCollectionChart(collection_slug) && !isCollectionObjectSlug(collection_slug)
}

// Mirrors backend `Interval::max_window_secs`. Must stay in sync with
// api/src/api/collection/chart.rs — values here only affect how aggressively
// the frontend splits requests; the server is the authority.
const MAX_WINDOW_SECS: Record<VWAPInterval, number> = {
	'1h': 90 * 86400,
	'4h': 180 * 86400,
	'1d': 5 * 365 * 86400
}

const BUCKET_SECS: Record<VWAPInterval, number> = {
	'1h': 3600,
	'4h': 14400,
	'1d': 86400
}

const fetcher = (url: string) =>
	fetch(url).then(async (r) => {
		if (!r.ok) throw new Error(`chart ${r.status}`)
		const data = await r.json()
		if (Array.isArray(data)) return data

		// The mark series: one bar per bucket, close = fair price in sats.
		if (Array.isArray(data?.candles)) {
			return (data.candles as Array<{ time: number; close: number }>)
				.filter((b) => b && typeof b.time === 'number' && typeof b.close === 'number' && b.close > 0)
				.map((b) => ({ time: b.time, vwap: b.close / 1e8 }))
		}

		const sales = Array.isArray(data?.sales) ? data.sales : null
		if (!sales) return data

		const parsed = new URL(url)
		const interval = (parsed.searchParams.get('interval') || '1d') as VWAPInterval
		const start = Number(parsed.searchParams.get('start') || 0)
		const end = Number(parsed.searchParams.get('end') || Number.MAX_SAFE_INTEGER)
		const bucket = BUCKET_SECS[interval] || BUCKET_SECS['1d']
		const buckets = new Map<number, { total: number; count: number }>()

		for (const sale of sales) {
			const ts = Number(sale.block_timestamp || sale.bought_at || 0)
			const price = Number(sale.price_sats || sale.satoshi_price || 0)
			if (!ts || !price || ts < start || ts >= end) continue
			const bucketStart = alignDown(ts, bucket)
			const current = buckets.get(bucketStart) || { total: 0, count: 0 }
			current.total += price
			current.count += 1
			buckets.set(bucketStart, current)
		}

		return Array.from(buckets.entries())
			.map(([time, value]) => ({ time, vwap: value.total / value.count / 1e8 }))
			.sort((a, b) => a.time - b.time)
	})

function alignDown(ts: number, step: number) {
	return ts - (ts % step)
}

// Paginated variant: splits the requested [start, end] into windows the server
// will accept and stitches the results. One SWR key per window so the CDN
// caches each chunk independently and consumers don't refetch everything when
// only the newest window moves.
export function useCollectionVWAPRange(
	collection_slug: string,
	interval: VWAPInterval,
	start: number,
	end: number,
	options: { keepPreviousData?: boolean } = {}
) {
	const windows = useMemo(() => {
		const out: Array<{ s: number; e: number }> = []
		const bucket = BUCKET_SECS[interval]
		const maxSpan = MAX_WINDOW_SECS[interval]
		if (!bucket || !maxSpan || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
			return out
		}

		const alignedEnd = alignDown(end, bucket) + bucket
		let cursor = alignDown(start, bucket)
		while (cursor < alignedEnd) {
			const winEnd = Math.min(cursor + maxSpan, alignedEnd)
			if (winEnd <= cursor) break
			out.push({ s: cursor, e: winEnd })
			cursor = winEnd
		}
		return out
	}, [interval, start, end])

	// SWR hooks must be called in a fixed order — we cap the count so we can
	// always issue the same number of hooks on every render. 6 windows covers
	// ~540d of hourly or ~30y of daily, which is well beyond any real series.
	const MAX_WINDOWS = 6
	const padded = [...windows, ...Array(Math.max(0, MAX_WINDOWS - windows.length)).fill(null)].slice(
		0,
		MAX_WINDOWS
	)
	const usesLegacyChart = shouldUseLegacyCollectionChart(collection_slug)
	const activeWindowCount = usesLegacyChart ? 1 : windows.length
	const results = padded.map((w, index) => {
		const chartPath =
			collection_slug && (usesLegacyChart ? index === 0 : !!w)
				? getCollectionChartPath(collection_slug, interval, w)
				: null
		const url = chartPath ? (chartPath.startsWith('/api/') ? chartPath : `${TURBO_BASE}${chartPath}`) : null
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useSWR<CollectionVWAPPoint[]>(url, fetcher, {
			revalidateOnFocus: false,
			keepPreviousData: options.keepPreviousData ?? true,
			dedupingInterval: 10_000,
			// See note on refreshInterval in useCollectionVWAP above — same
			// rationale: blow past the service worker's stale response.
			refreshInterval: 60_000
		})
	})

	// The hook result wrappers are rebuilt by the fixed hook loop on every
	// render, but each SWR `data` reference is stable until that page changes.
	// Memoize against those page references so chart hover/resize renders do not
	// look like new data and rebuild the entire lightweight-charts instance.
	const page0 = results[0]?.data
	const page1 = results[1]?.data
	const page2 = results[2]?.data
	const page3 = results[3]?.data
	const page4 = results[4]?.data
	const page5 = results[5]?.data
	const merged = useMemo(() => {
		const all: CollectionVWAPPoint[] = []
		for (const page of [page0, page1, page2, page3, page4, page5]) {
			if (Array.isArray(page)) all.push(...page)
		}
		// Windows are contiguous and server-sorted, but guard against dupes at
		// boundaries (inclusive start vs exclusive end snap to the same bucket
		// if start/end align).
		all.sort((a, b) => a.time - b.time)
		const out: CollectionVWAPPoint[] = []
		let last = -1
		for (const p of all) {
			if (p.time !== last) out.push(p)
			last = p.time
		}
		return out
	}, [page0, page1, page2, page3, page4, page5])

	const error = results.find((r) => r.error)?.error
	const isLoading = results.some((r, i) => i < activeWindowCount && !r.data && !r.error)

	return { data: merged, error, isLoading }
}
